import React, { useRef } from 'react';
import type { Variants } from 'framer-motion';
import { motion, useInView, useReducedMotion } from 'framer-motion';

export interface WordRevealProps {
  text: string;
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'p' | 'span' | 'div';
  className?: string;
  highlightWords?: string[];
  highlightClassName?: string;
  stagger?: number;
  delay?: number;
  once?: boolean;
}

const containerVariants: Variants = {
  hidden: {},
  visible: (custom: { stagger: number; delay: number }) => ({
    transition: {
      staggerChildren: custom.stagger,
      delayChildren: custom.delay,
    },
  }),
};

const wordVariants: Variants = {
  hidden: {
    y: '100%',
    opacity: 0,
    filter: 'blur(10px)',
  },
  visible: {
    y: '0%',
    opacity: 1,
    filter: 'blur(0px)',
    transition: {
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1], // Luxurious cubic-bezier curve
    },
  },
};

export function WordReveal({
  text,
  as: Component = 'h2',
  className = '',
  highlightWords = [],
  highlightClassName = 'text-[#15803d]',
  stagger = 0.045,
  delay = 0,
  once = true,
}: WordRevealProps) {
  const containerRef = useRef<HTMLHeadingElement>(null);
  const isInView = useInView(containerRef, {
    margin: '-10% 0px -10% 0px',
    once,
  });
  const shouldReduceMotion = useReducedMotion();

  // Split text into words while keeping space markers
  const words = text.split(' ');

  const cleanWord = (word: string) => word.replace(/^[^\w\s]+|[^\w\s]+$/g, '').toLowerCase();

  const isHighlighted = (word: string) => {
    if (!highlightWords.length) return false;
    const cleaned = cleanWord(word);
    return highlightWords.some((h) => cleanWord(h) === cleaned || word.includes(h));
  };

  if (shouldReduceMotion) {
    return (
      <Component className={className}>
        {words.map((word, i) => (
          <React.Fragment key={i}>
            <span className={isHighlighted(word) ? highlightClassName : undefined}>{word}</span>
            {i < words.length - 1 ? ' ' : ''}
          </React.Fragment>
        ))}
      </Component>
    );
  }

  return (
    <Component ref={containerRef} className={className}>
      <motion.span
        className="inline flex-wrap gap-x-[0.28em] gap-y-1"
        variants={containerVariants}
        initial="hidden"
        animate={isInView ? 'visible' : 'hidden'}
        custom={{ stagger, delay }}
      >
        {words.map((word, i) => {
          const highlighted = isHighlighted(word);
          return (
            <span key={i} className="inline-block overflow-hidden align-top py-[0.1em] -my-[0.1em]">
              <motion.span
                variants={wordVariants}
                className={`inline-block gpu-layer ${highlighted ? highlightClassName : ''}`}
              >
                {word}
              </motion.span>
              {i < words.length - 1 && <span className="inline-block whitespace-pre">&nbsp;</span>}
            </span>
          );
        })}
      </motion.span>
    </Component>
  );
}
